/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { styled } from "@mui/system";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { BackButton } from "../../../components/BackButton";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import ContactCard from "../../../components/ContactCard";
import { PageContainer } from "../../../components/layout/PageContainer";
import { Loader } from "../../../components/Loader";
import { useAppDispatch } from "../../../store/rootStore";
import TestIds from "../../../testIds";
import { CONTACT_CARD_TITLE, CONTENT_LAYOUT_WIDTH } from "../../../util/consts";
import { log } from "../../../util/log";
import { getQueryParamValue, getRelativeRouteWithQueryParams } from "../../../util/routing";
import { useBranch } from "../../../util/useBranch";
import { selectContactInfo, selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import QuoteProvider from "../../quotes/components/QuoteModal/useQuoteModal";
import { ProductDetailsTabs } from "../components/ProductDetails/ProductDetailsTabs";
import { ProductInfo, QueryVariant, safeQuantity } from "../components/ProductDetails/ProductInfo";
import {
  EProductLocation,
  IOpenProductDetailsNavigationState,
} from "../components/ProductDetails/utils/openProductDetails";
import { PRODUCTS_CART_ROUTE, PRODUCTS_ROUTES, PRODUCTS_TAB_0_ROUTE } from "../constants";
import { StocksSlice } from "../storage/stocks.slice";
import { ProductDetailsApiResponse } from "../storage/types";
import { fetchProductDetail } from "../utils/fetchProductDetail";

const Wrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  width: "100%",
});

const ProductDetails = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { masterProductId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryVariant = useMemo<QueryVariant>(
    () => ({
      size: searchParams.get("size") ?? "",
      colour: searchParams.get("colour") ?? "",
      qty: safeQuantity(searchParams.get("qty")),
    }),
    [searchParams]
  );

  const setQueryVariant: Dispatch<SetStateAction<QueryVariant>> = (next) => {
    const value = typeof next === "function" ? next(queryVariant) : next;

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("size", value.size);
        params.set("colour", value.colour);
        params.set("qty", value.qty.toString());
        return params;
      },
      { replace: true }
    );
  };

  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const relationshipOwner = useSelector(selectContactInfo);
  const [item, setItem] = useState<ProductDetailsApiResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError>();
  const [locationState, setLocationState] = useState<IOpenProductDetailsNavigationState>();
  const branchId = useBranch();

  // Fetch product details based on query parameters and state
  useEffect(() => {
    if (masterProductId) {
      setIsLoading(true);
      fetchProductDetail(
        masterProductId,
        currentAccount?.accountNumber,
        branchId,
        queryVariant.size,
        queryVariant.colour,
        location.state || locationState
      )
        .then((newItem) => {
          setItem(newItem);
          dispatch(StocksSlice.actions.addStock(newItem));
          setError(undefined);
          setIsLoading(false);
        })
        .catch((err) => {
          setError(err as AxiosError);
          log("error", error);
        });
    }
  }, [currentAccount?.accountNumber, masterProductId, branchId, dispatch]);

  useEffect(() => {
    if (!item) {
      return;
    }

    const size: string = queryVariant.size || item.sizeDescription || "";
    const colour: string = queryVariant.colour || item.colourDescription || "";

    if (size !== queryVariant.size && colour !== queryVariant.colour) {
      setQueryVariant({
        ...queryVariant,
        size,
        colour,
      });
    }
  }, [item]);

  useEffect(() => {
    if (location.state) {
      setLocationState(location.state);
    }
  }, [location.state]);

  const getBreadcrumbURL = (categoryType: "main" | "sub"): string => {
    let category: string = item?.category ?? "";
    if (categoryType === "sub") {
      category += `>${item?.subCategory}`;
    }

    const tab: string =
      (locationState?.previousRelativeUrl &&
        getQueryParamValue(locationState?.previousRelativeUrl, "tab")) ??
      "0";

    return getRelativeRouteWithQueryParams(PRODUCTS_ROUTES.BASE, {
      tab,
      category,
    });
  };

  const isCartProduct = (): boolean => locationState?.productLocation === EProductLocation.CART;

  const navigateToBack = (): void => {
    if (isCartProduct()) {
      navigate(PRODUCTS_CART_ROUTE);
    } else {
      const backRelativeUrl: string = locationState?.previousRelativeUrl ?? PRODUCTS_TAB_0_ROUTE;
      navigate(backRelativeUrl);
    }
  };

  return (
    <PageContainer
      data-testid={TestIds.pages.productDetails}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        maxWidth: CONTENT_LAYOUT_WIDTH,
      }}
    >
      {isLoading && <Loader />}
      {!isLoading && item && (
        <Wrapper sx={{ gap: 4 }}>
          <Wrapper>
            <BackButton
              text={isCartProduct() ? "Back to cart" : "Back to shop"}
              onClick={navigateToBack}
              data-testid={TestIds.components.backButton.productDetails}
            />
            <Breadcrumbs
              items={[
                {
                  text: item.category,
                  href: getBreadcrumbURL("main"),
                },
                {
                  text: item.subCategory ?? "",
                  href: getBreadcrumbURL("sub"),
                },
                { text: item.name },
              ]}
            />
            <ProductInfo
              item={item}
              queryVariant={queryVariant}
              setQueryVariant={setQueryVariant}
              data-testid={TestIds.feat.product.details.container}
            />
          </Wrapper>
          <ProductDetailsTabs item={item} />
          <ContactCard title={CONTACT_CARD_TITLE.productDetails} {...relationshipOwner} />
        </Wrapper>
      )}
    </PageContainer>
  );
};

export default () => QuoteProvider(ProductDetails);
